import { useEffect, useRef, useState } from "react";
import EventListItem from "./EventListItem";
import styles from "./EventSpotlight.module.css";

export const EVENT_SPOTLIGHT_HEADING_HEIGHT = 20;
// Titre (20), marge haute (4), puis 8px au-dessus et sous l'item.
export const EVENT_SPOTLIGHT_FRAME_HEIGHT = EVENT_SPOTLIGHT_HEADING_HEIGHT + 20;
const AUTO_ADVANCE_MS = 6000;

export default function EventSpotlight({ events, title = "À la une", onSelectEvent, interestsByEventId = {}, onToggleInterest, isInterestPending, distanceOriginCenter, selectedEventId, readOnly = false }) {
  const trackRef = useRef(null);
  const rootRef = useRef(null);
  const activeIdRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isPointerDown, setIsPointerDown] = useState(false);
  const [canAutoPlay, setCanAutoPlay] = useState(false);

  useEffect(() => {
    const motion = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    let inView = typeof IntersectionObserver === "undefined";
    const update = () => setCanAutoPlay(inView && !document.hidden && !motion?.matches);
    const observer = typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting && entry.intersectionRatio >= 0.5;
      update();
    }, { threshold: [0, 0.5] });
    if (rootRef.current) observer?.observe(rootRef.current);
    document.addEventListener("visibilitychange", update);
    motion?.addEventListener?.("change", update);
    update();
    return () => {
      observer?.disconnect();
      document.removeEventListener("visibilitychange", update);
      motion?.removeEventListener?.("change", update);
    };
  }, []);

  useEffect(() => {
    if (events.length < 2 || !canAutoPlay || isPaused || isHovered || isFocused || isPointerDown) return undefined;
    const timer = window.setTimeout(() => {
      const next = (activeIndex + 1) % events.length;
      const track = trackRef.current;
      track?.scrollTo({ left: next * track.clientWidth, top: 0, behavior: next === 0 ? "auto" : "smooth" });
    }, AUTO_ADVANCE_MS);
    return () => window.clearTimeout(timer);
  }, [activeIndex, events.length, canAutoPlay, isPaused, isHovered, isFocused, isPointerDown]);

  useEffect(() => {
    const index = Math.max(0, events.findIndex((event) => event.id === activeIdRef.current));
    activeIdRef.current = events[index]?.id;
    setActiveIndex(index);
    const track = trackRef.current;
    track?.scrollTo({ left: index * track.clientWidth, top: 0, behavior: "auto" });
  }, [events]);

  const navigate = (direction) => {
    const index = Math.max(0, Math.min(events.length - 1, activeIndex + direction));
    const track = trackRef.current;
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    track?.scrollTo({ left: index * track.clientWidth, top: 0, behavior: reducedMotion ? "auto" : "smooth" });
  };

  return (
    <section ref={rootRef} className={styles.root} aria-label={title} aria-roledescription="carrousel"
      onPointerEnter={(event) => { if (event.pointerType === "mouse") setIsHovered(true); }}
      onPointerLeave={() => { setIsHovered(false); setIsPointerDown(false); }}
      onPointerDown={() => setIsPointerDown(true)} onPointerUp={() => setIsPointerDown(false)}
      onPointerCancel={() => setIsPointerDown(false)} onLostPointerCapture={() => setIsPointerDown(false)}
      onFocusCapture={() => setIsFocused(true)}
      onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setIsFocused(false); }}
      style={{ "--spotlight-heading-height": `${EVENT_SPOTLIGHT_HEADING_HEIGHT}px` }}>
      <div className={styles.header}>
        <h2 className={styles.heading}>{title}</h2>
        {events.length > 1 && <div className={styles.headerControls}>
          <span className={styles.counter} role="status" aria-live={isPaused ? "polite" : "off"}
            aria-label={`Sortie ${activeIndex + 1} sur ${events.length}`}>
            {activeIndex + 1} / {events.length}
          </span>
          <button type="button" className={styles.playback}
            aria-label={isPaused ? "Reprendre le défilement automatique" : "Mettre en pause le défilement automatique"}
            onClick={() => { setIsPaused((paused) => !paused); setIsFocused(false); }}>
            <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              {isPaused ? <path d="m5 3 8 5-8 5z" /> : <path d="M4 3h3v10H4zm5 0h3v10H9z" />}
            </svg>
          </button>
        </div>}
      </div>
      <div className={styles.content}>
      <div className={styles.track} ref={trackRef} onScroll={(event) => {
        const track = event.currentTarget;
        const index = Math.max(0, Math.min(events.length - 1, Math.round(track.scrollLeft / (track.clientWidth || 1))));
        activeIdRef.current = events[index]?.id;
        setActiveIndex(index);
      }}>
        {events.map((event, index) => (
          <div className={styles.slide} key={event.id} role="group"
            aria-label={`Sortie mise en avant ${index + 1} sur ${events.length}`}
            inert={index !== activeIndex ? "" : undefined}>
            <EventListItem evt={event} onClick={onSelectEvent} disableHoverMotion
              distanceOriginCenter={distanceOriginCenter}
              isSelected={selectedEventId === event.id}
              isInterested={!readOnly && !!interestsByEventId[event.id]}
              isInterestPending={!readOnly && !!isInterestPending?.(event.id, "INTERESTED")}
              onToggleInterest={readOnly ? undefined : onToggleInterest}
              readOnly={readOnly} />
          </div>
        ))}
      </div>
      {events.length > 1 && <>
        <button type="button" className={`${styles.arrow} ${styles.previous}`}
          aria-label="Sortie mise en avant précédente" disabled={activeIndex === 0}
          onClick={() => navigate(-1)}>
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m14 6-6 6 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <button type="button" className={`${styles.arrow} ${styles.next}`}
          aria-label="Sortie mise en avant suivante" disabled={activeIndex >= events.length - 1}
          onClick={() => navigate(1)}>
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m10 6 6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </>}
      </div>
    </section>
  );
}
